import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, Target, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayerStatsPage() {
  const { token, organization } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organization) {
      fetchStats();
    }
  }, [organization]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats/players/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="stats-page" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
            Player Stats
          </h1>
          <p className="text-gray-600 mt-2">Individual player statistics and rankings</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !stats?.all_players?.length ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <BarChart3 className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-black text-lg font-medium mb-2">No stats recorded</h3>
            <p className="text-gray-500">Add players and record match stats to see rankings</p>
          </div>
        ) : (
          <Tabs defaultValue="scorers" className="space-y-6">
            <TabsList className="bg-white border border-gray-200">
              <TabsTrigger 
                value="scorers" 
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"
              >
                <Target size={16} className="mr-2" />
                Top Scorers
              </TabsTrigger>
              <TabsTrigger 
                value="assists" 
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"
              >
                <Users size={16} className="mr-2" />
                Top Assists
              </TabsTrigger>
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"
              >
                <BarChart3 size={16} className="mr-2" />
                All Players
              </TabsTrigger>
            </TabsList>

            {/* Top Scorers */}
            <TabsContent value="scorers">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200 hover:bg-transparent">
                      <TableHead className="text-gray-600 uppercase text-xs font-bold w-12">#</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Player</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Team</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">Goals</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.top_scorers.map((player, index) => (
                      <TableRow key={player.player_id} className="border-gray-100 table-row-hover">
                        <TableCell>
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-500 text-black' :
                            index === 1 ? 'bg-gray-400 text-black' :
                            index === 2 ? 'bg-orange-600 text-black' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {index + 1}
                          </span>
                        </TableCell>
                        <TableCell className="text-black font-medium">{player.name}</TableCell>
                        <TableCell className="text-gray-600">{player.team_name}</TableCell>
                        <TableCell className="text-center">
                          <span className="text-emerald-500 font-bold text-xl">{player.goals}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Top Assists */}
            <TabsContent value="assists">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200 hover:bg-transparent">
                      <TableHead className="text-gray-600 uppercase text-xs font-bold w-12">#</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Player</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Team</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">Assists</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.top_assists.map((player, index) => (
                      <TableRow key={player.player_id} className="border-gray-100 table-row-hover">
                        <TableCell>
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-500 text-black' :
                            index === 1 ? 'bg-gray-400 text-black' :
                            index === 2 ? 'bg-orange-600 text-black' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {index + 1}
                          </span>
                        </TableCell>
                        <TableCell className="text-black font-medium">{player.name}</TableCell>
                        <TableCell className="text-gray-600">{player.team_name}</TableCell>
                        <TableCell className="text-center">
                          <span className="text-blue-500 font-bold text-xl">{player.assists}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* All Players */}
            <TabsContent value="all">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200 hover:bg-transparent">
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">#</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Player</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Team</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold">Pos</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">GP</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">G</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">A</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">YC</TableHead>
                      <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">RC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.all_players.map((player) => (
                      <TableRow key={player.player_id} className="border-gray-100 table-row-hover">
                        <TableCell className="text-emerald-500 font-mono font-bold">{player.number}</TableCell>
                        <TableCell className="text-black font-medium">{player.name}</TableCell>
                        <TableCell className="text-gray-600">{player.team_name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
                            {player.position}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-gray-700 font-medium">{player.games_played || 0}</TableCell>
                        <TableCell className="text-center text-emerald-500 font-medium">{player.goals}</TableCell>
                        <TableCell className="text-center text-blue-500 font-medium">{player.assists}</TableCell>
                        <TableCell className="text-center text-yellow-500">{player.yellow_cards}</TableCell>
                        <TableCell className="text-center text-red-500">{player.red_cards}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
